/**
 * Sign in.
 *
 * Clerk's own form, mounted into our own page, in our own colours. Not a redirect
 * to a hosted page: a new customer arriving at a third party's domain half way
 * through signing up is the point at which a fair number of them stop, and the
 * three lines of copy beside the form are worth more than the redirect saves.
 *
 * The copy is there because this is the first screen of the product for somebody
 * who has just clicked "Start free" on the home page, and a bare form with a logo
 * above it does not continue the sentence that page was making.
 */
import { useEffect, useRef } from "react";
import { legalUrl, siteUrl } from "../api.js";
import { clerk } from "../clerk.js";

export function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const mount = useRef<HTMLDivElement>(null);
  const params = new URLSearchParams(window.location.search);
  const invited = params.has("invited");
  /*
   * An invitation to somebody with no account arrives with `__clerk_status=sign_up`.
   * Clerk's sign-in form would pass that on to a sign-up form at an address this
   * one-screen page does not have, so the sign-up form is mounted directly: it reads
   * the ticket from the address itself and fixes the email to the invited one.
   */
  const signingUp = params.get("__clerk_status") === "sign_up";

  useEffect(() => {
    const instance = clerk();
    const element = mount.current;
    if (!instance || !element) return;
    // Back to this address afterwards — which carries `?invited=` when there is one —
    // rather than Clerk's default of the root, which would drop it. Clerk's own
    // parameters are left off: the ticket in them has been used by then.
    const here = new URL(window.location.href);
    for (const key of [...here.searchParams.keys()]) if (key.startsWith("__clerk_")) here.searchParams.delete(key);
    const back = `${here.pathname}${here.search}`;
    // Virtual routing: the application notices the session and moves on by itself,
    // which is what keeps this one screen rather than a redirect round trip.
    if (signingUp) instance.mountSignUp(element, { routing: "virtual", forceRedirectUrl: back } as never);
    else instance.mountSignIn(element, { routing: "virtual", signUpUrl: undefined, forceRedirectUrl: back, signUpForceRedirectUrl: back } as never);
    const unsubscribe = instance.addListener(() => {
      if (instance.session) onSignedIn();
    });
    return () => {
      unsubscribe?.();
      try {
        if (signingUp) instance.unmountSignUp(element);
        else instance.unmountSignIn(element);
      } catch {
        // Unmounting a component Clerk has already torn down throws, and there is
        // nothing to do about it.
      }
    };
  }, [onSignedIn, signingUp]);

  return (
    <main className="main">
      <div className="wrap" style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) auto", gap: "clamp(28px, 6vw, 80px)", alignItems: "center", minHeight: "70vh" }}>
        <div className="stack" style={{ gap: 18, maxWidth: "46ch" }}>
          {/* Somebody who arrived here by mistake, or wants to read more first, needs a way back to the website. */}
          <a href={siteUrl("/")} aria-label="BroadPaper website" style={{ alignSelf: "flex-start", lineHeight: 0 }}>
            <img src="/logo.svg" alt="BroadPaper" style={{ height: 28 }} />
          </a>
          {invited ? (
            <>
              <h1>You've been invited to a workspace.</h1>
              <p className="muted" style={{ fontSize: 17 }}>
                {signingUp ? "Create your sign-in with the email address the invitation was sent to" : "Sign in"}, and you will go straight to it — the
                data, designs and documents your colleagues have already made.
              </p>
            </>
          ) : (
            <>
              <h1>Turn your business data into professional documents.</h1>
              <p className="muted" style={{ fontSize: 17 }}>
                Bring in a spreadsheet, choose a design, and download a polished PDF. BroadPaper works out what your columns mean and fills the document
                in for you.
              </p>
            </>
          )}
          <ul className="stack small muted" style={{ gap: 8, listStyle: "none", padding: 0, margin: 0 }}>
            <li>Free to use with your own data — no card, nothing to install.</li>
            <li>Finished invoices, quotes, receipts, statements and more — or start from an empty page.</li>
            <li>Your data is private, and is never used to train anything.</li>
          </ul>
        </div>
        {/*
          The form keeps its own width. Clerk sizes it, and stretching it across a
          wide column makes a six-field form look like a wizard.

          The policies sit under it because this is the first page of the product,
          where somebody agrees to them — and the page Google's verification of the
          Drive connection checks for a privacy policy.
        */}
        <div className="stack" style={{ gap: 12, minWidth: 340 }}>
          <div ref={mount} />
          <p className="small muted" style={{ maxWidth: "40ch", margin: 0 }}>
            By signing in you agree to the{" "}
            <a href={legalUrl("/legal/terms/")} target="_blank" rel="noreferrer">
              terms
            </a>
            . The{" "}
            <a href={legalUrl("/legal/privacy/")} target="_blank" rel="noreferrer">
              privacy policy
            </a>{" "}
            says what BroadPaper keeps and why.
          </p>
        </div>
      </div>
    </main>
  );
}
